import React from 'react';

// components
import CrossIcon from '../../icons/CrossIcon';

const ModalDialog = ({ header, visible, onHide, children, onSubmit, additionalClasses = [], width = 620 }) => {
    const classes = ['ModalDialog', ...additionalClasses];
    const dialogRef = React.useRef(null);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onHide();
        }
    };

    React.useEffect(() => {
        const handleKeyDown = (e) => {
            // Handle Escape key to close modal
            if (e.key === 'Escape' && visible) {
                onHide();
                return;
            }

            // Handle Enter key for form submission
            if (e.key === 'Enter') {
                // Allow Enter to create new lines in textareas and rich editors
                if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
                    return;
                }

                // A field inside a <form> already submits it natively, which also
                // honours a disabled submit button. Submitting here as well would
                // fire the request twice and bypass that guard.
                if (e.target.closest?.('form')) {
                    return;
                }

                if (onSubmit) {
                    onSubmit();
                }
            }
        };

        if (visible) {
            document.addEventListener('keydown', handleKeyDown);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [visible, onHide]);

    // Handle dialog show/hide using native methods.
    //
    // Re-opening has to start from a closed dialog. Moving the <dialog> in the
    // DOM — which React does whenever the list a modal is rendered inside gets
    // re-sorted, e.g. the projects grid after a rename — takes it out of the top
    // layer: the element keeps `open` but is no longer modal, and showModal()
    // throws on an open non-modal dialog ("The dialog is already open as a
    // non-modal dialog"). Closing first makes the call idempotent and restores
    // modality after such a move.
    React.useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;

        if (dialog.open) {
            dialog.close();
        }

        if (visible) {
            dialog.showModal();
        }

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, [visible]);

    if (!visible) return null;

    return (
        <dialog
            ref={dialogRef}
            className={classes.join(' ')}
            style={{
                '--modal-max-width': `${parseInt(width)}px`
            }}
            onClick={handleBackdropClick}
        >
            <div className="ModalDialog__inner">
                <div className="ModalDialog__header">
                    <span className="ModalDialog__title">{header}</span>
                    <button
                        onClick={onHide}
                        className="ModalDialog__closeIcon"
                        aria-label="Close modal">
                        <CrossIcon/>
                    </button>
                </div>
                <div className="ModalDialog__body">
                    {children}
                </div>
            </div>
        </dialog>
    );
};

export default ModalDialog;